// EDEN · the teacher LIBRARY — the data behind the Books tab and the extra rows on every lesson's Books card.
//
// T1 review (owner, 9 Oct 2026): "we have all the resources — books. We should have a tab called Books … all
// resources should be e-books style and available." Three generated files (scripts/build-library.mjs), each
// loaded once and code-split like lesson-refs.json:
//   curriculum/library.json        every online book, shelved by grade and purpose, with contents and audio
//   curriculum/lesson-extras.json  per lesson: workbook answers, this fortnight's story, test audio, practice sheets
//   curriculum/pack-glance.json    per lesson: the Teacher Pack as one screen
// Nothing here is a page picture or a sound: those stay behind the signed-in /m/ gate (functions/m/[[path]].ts).

import type { GuideTarget, PageSeg, ReaderTarget, Track } from "./book-reader";
import "./styles/library.css";

export interface LibContent {
  label: string;
  pdf: PageSeg[];
  key?: PageSeg[];
  tracks?: string[];
  file?: number;
  key_file?: number;
  key_none?: string;
  key_note?: string;
  /** answers kept in another book: level 1 of Oxford Primary Skills (a book per unit), a workbook's answer key */
  answers_book?: string;
  /** …and, where known, just this part's pages in it (a workbook unit's answers) */
  answers_pdf?: PageSeg[];
}
export interface LibBook {
  title: string;
  section: string;
  pages: number;
  pdf: PageSeg[];
  contents?: LibContent[];
  tracks?: Track[];
  answers?: string;
  guide?: string;
  note?: string;
  tier?: string;
  downloads?: boolean;
}
export interface LibShelf { key: string; title: string; hint: string; ids: string[] }
export interface LibraryFile { schema: number; grades: Record<string, LibShelf[]>; books: Record<string, LibBook> }

export interface ExtraTarget extends GuideTarget { sub?: string; tracks?: Track[]; answers?: GuideTarget; no_audio?: string }
export interface PracticeExtra {
  book_id: string; book: string; label: string; pdf: PageSeg[]; key?: PageSeg[]; tracks?: Track[];
  file?: number; key_file?: number; key_none?: string;
}
export interface LessonExtras {
  wb_answers?: GuideTarget;
  cb_answers?: GuideTarget;
  cb_tracks?: Track[];
  reading?: ExtraTarget[];
  test_audio?: { row: number; book_id: string; tracks: Track[] }[];
  practice?: PracticeExtra[];
}
interface ExtrasFile { schema: number; nodes: Record<string, LessonExtras> }

export interface PackGlance {
  oneliner: string;
  objectives: string;
  strands: { key: "coursebook" | "thread" | "fluency"; periods: number | null; note: string }[];
  steps: { label: string; text: string }[];
  question: string;
}
interface GlanceFile { schema: number; nodes: Record<string, PackGlance> }

// ── loaders: one fetch each, a failed load is not remembered (the next screen tries again) ──────────
let libP: Promise<LibraryFile | null> | null = null;
let extrasP: Promise<ExtrasFile | null> | null = null;
let glanceP: Promise<GlanceFile | null> | null = null;
let extrasLoaded: ExtrasFile | null = null;

export function loadLibrary(): Promise<LibraryFile | null> {
  if (!libP) {
    libP = import("../curriculum/library.json")
      .then((m) => (m.default ?? m) as unknown as LibraryFile)
      .catch(() => { libP = null; return null; });
  }
  return libP;
}
export function loadLessonExtras(): Promise<ExtrasFile | null> {
  if (!extrasP) {
    extrasP = import("../curriculum/lesson-extras.json")
      .then((m) => (extrasLoaded = (m.default ?? m) as unknown as ExtrasFile))
      .catch(() => { extrasP = null; return null; });
  }
  return extrasP;
}
export function loadPackGlance(): Promise<GlanceFile | null> {
  if (!glanceP) {
    glanceP = import("../curriculum/pack-glance.json")
      .then((m) => (m.default ?? m) as unknown as GlanceFile)
      .catch(() => { glanceP = null; return null; });
  }
  return glanceP;
}
/** The extras for a lesson once loadLessonExtras() has resolved (null before, or for a lesson with none). */
export function extrasFor(nodeId: string | null | undefined): LessonExtras | null {
  return nodeId ? extrasLoaded?.nodes[nodeId] ?? null : null;
}
export async function glanceFor(nodeId: string | null | undefined): Promise<PackGlance | null> {
  if (!nodeId) return null;
  return (await loadPackGlance())?.nodes[nodeId] ?? null;
}

const LG_TEACHING = /^(?:LG[1-6]|LGB[12])-SB$/;

/** Does this part of a book come with answers (its own key, a key in another book, or the book's answer key)? */
export function hasAnswers(book: LibBook, c: LibContent, lib: LibraryFile): boolean {
  return !!(c.key?.length || (c.answers_book && lib.books[c.answers_book]) || (book.answers && lib.books[book.answers]));
}

// ── reader targets ───────────────────────────────────────────────────────────────────────────────
/** The signed-in download of a bundle's original Word file. */
export function fileHref(bookId: string, n: number): string {
  return `/m/${encodeURIComponent(bookId)}/file/${n}`;
}

/** A practice worksheet: its pages, its audio, its answer key one tap away, its Word file to download. */
export function practiceTarget(p: PracticeExtra): ReaderTarget {
  const t: ReaderTarget = { book: p.book, bookId: p.book_id, label: p.label, pages: p.pdf, tracks: p.tracks };
  if (p.key?.length) t.guide = { book_id: p.book_id, book: p.book, label: `${p.label} · answer key`, pdf: p.key };
  const downloads = [];
  if (p.file) downloads.push({ label: "Word file", href: fileHref(p.book_id, p.file) });
  if (p.key_file) downloads.push({ label: "Answer key (Word)", href: fileHref(p.book_id, p.key_file) });
  if (downloads.length) t.downloads = downloads;
  return t;
}

/** One entry of a book's contents, opened from the Books tab: these pages, the whole book one tap away. */
export function contentTarget(bookId: string, book: LibBook, c: LibContent, lib: LibraryFile): ReaderTarget {
  const trackSet = new Set(c.tracks ?? []);
  const tracks = c.tracks?.length ? (book.tracks ?? []).filter((t) => trackSet.has(t.id)) : undefined;
  const t: ReaderTarget = { book: book.title, bookId, label: c.label, pages: c.pdf, tracks, from: "library" };
  if (book.pdf.length && (book.pdf[0]![0] !== c.pdf[0]![0] || book.pdf[book.pdf.length - 1]![1] !== c.pdf[c.pdf.length - 1]![1])) {
    t.unit = { label: book.title, pages: book.pdf };
  }
  if (c.key?.length) t.guide = { book_id: bookId, book: book.title, label: `${c.label} · answer key`, pdf: c.key };
  else if (c.answers_book && lib.books[c.answers_book]) {
    const a = lib.books[c.answers_book]!;
    t.guide = c.answers_pdf?.length
      ? { book_id: c.answers_book, book: a.title, label: `${c.label} · answers`, pdf: c.answers_pdf }
      : { book_id: c.answers_book, book: a.title, label: a.title, pdf: a.pdf };
  } else if (book.answers && lib.books[book.answers]) {
    const a = lib.books[book.answers]!;
    t.guide = { book_id: book.answers, book: a.title, label: a.title, pdf: a.pdf };
  }
  const downloads = [];
  if (c.file) downloads.push({ label: "Word file", href: fileHref(bookId, c.file) });
  if (c.key_file) downloads.push({ label: "Answer key (Word)", href: fileHref(bookId, c.key_file) });
  if (downloads.length) t.downloads = downloads;
  return t;
}

/** The whole book, from its first page. Audio: the book's whole track list (the page prints the track number). */
export function wholeBookTarget(bookId: string, book: LibBook, lib: LibraryFile): ReaderTarget {
  const t: ReaderTarget = { book: book.title, bookId, label: "The whole book", pages: book.pdf, from: "library" };
  // Let's Go / Let's Begin Student Books bring each page's own tracks with its guide notes; the rest list the CD.
  if (book.tracks?.length && !LG_TEACHING.test(bookId)) t.tracks = book.tracks;
  if (book.answers && lib.books[book.answers]) {
    const a = lib.books[book.answers]!;
    t.guide = { book_id: book.answers, book: a.title, label: a.title, pdf: a.pdf };
  }
  return t;
}
